import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Loader2,
  CheckCircle2, XCircle, Clock, Timer, Ban, Play,
  X, ShipWheel, ShieldCheck, Workflow, ChevronDown, Check, Webhook,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiClient, type WorkflowExecution, type CustomBlock } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';

import slackColorIcon from '../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import jiraIcon from '../assets/icons/jira.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPExecIcon } from '../assets/icons/gcp.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import cloudflareIcon from '../assets/icons/cloudflare.svg';

type ExecutionRow = WorkflowExecution & { workflow_name: string; workflow_integrations?: string[] };

const PAGE_SIZE = 10;
const POLL_INTERVAL = 30000;

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'waiting_approval', label: 'Awaiting Approval' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TRIGGER_OPTIONS = [
  { value: 'kubernetes', label: 'Kubernetes', icon: 'kubernetes' },
  { value: 'github', label: 'GitHub', icon: 'github' },
  { value: 'aws', label: 'AWS', icon: 'aws' },
  { value: 'datadog', label: 'Datadog', icon: 'datadog' },
  { value: 'cloudflare', label: 'Cloudflare', icon: 'cloudflare' },
  { value: 'pagerduty', label: 'PagerDuty', icon: 'pagerduty' },
  { value: 'posthog', label: 'PostHog', icon: 'posthog' },
  { value: 'vercel', label: 'Vercel', icon: 'vercel' },
  { value: 'railway', label: 'Railway', icon: 'railway' },
  { value: 'flyio', label: 'Fly.io', icon: 'flyio' },
  { value: 'nebius', label: 'Nebius', icon: 'nebius' },
  { value: 'beam', label: 'Beam', icon: 'beam' },
  { value: 'daytona', label: 'Daytona', icon: 'daytona' },
  { value: 'supabase', label: 'Supabase', icon: 'supabase' },
  { value: 'planetscale', label: 'PlanetScale', icon: 'planetscale' },
  { value: 'neon', label: 'Neon', icon: 'neon' },
  { value: 'clickhouse', label: 'ClickHouse', icon: 'clickhouse' },
  { value: 'jenkins', label: 'Jenkins', icon: 'jenkins' },
  { value: 'circleci', label: 'CircleCI', icon: 'circleci' },
  { value: 'sonarcloud', label: 'SonarCloud', icon: 'sonarcloud' },
  { value: 'okta', label: 'Okta', icon: 'okta' },
  { value: 'databricks', label: 'Databricks', icon: 'databricks' },
  { value: 'gcp', label: 'Google Cloud', icon: 'gcp' },
  { value: 'terraform', label: 'Terraform Cloud', icon: 'terraform' },
  { value: 'pulumi', label: 'Pulumi Cloud', icon: 'pulumi' },
  { value: 'vault', label: 'HashiCorp Vault', icon: 'vault' },
  { value: 'infisical', label: 'Infisical', icon: 'infisical' },
  { value: 'custom_webhook', label: 'Custom Webhook', icon: 'custom' },
  { value: 'schedule', label: 'Schedule', icon: 'schedule' },
];

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  renderOption,
  className,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  renderOption?: (option: { value: string; label: string }) => React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    );
  };

  const displayText = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-8 text-sm justify-between font-normal gap-1", className)}>
          <span className="truncate">{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-1 max-h-[min(400px,_var(--radix-popover-content-available-height))] overflow-y-auto" align="start">
        {options.map(o => (
          <button
            key={o.value}
            onClick={() => toggle(o.value)}
            className="flex items-start gap-2 w-full px-2 py-1.5 text-sm text-left rounded-sm hover:bg-accent cursor-pointer"
          >
            <div className={cn(
              "w-4 h-4 border rounded flex items-center justify-center shrink-0",
              selected.includes(o.value) ? "bg-primary border-primary" : "border-muted-foreground/30"
            )}>
              {selected.includes(o.value) && <Check className="w-3 h-3 text-primary-foreground" />}
            </div>
            {renderOption ? renderOption(o) : <span>{o.label}</span>}
          </button>
        ))}
        {selected.length > 0 && (
          <>
            <div className="border-t my-1" />
            <button
              onClick={() => onChange([])}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-muted-foreground cursor-pointer"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="outline" className="text-green-600 border-green-300 dark:border-green-700 gap-1"><CheckCircle2 className="w-3 h-3" />Completed</Badge>;
    case 'failed':
      return <Badge variant="outline" className="text-red-600 border-red-300 dark:border-red-700 gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
    case 'running':
      return <Badge variant="outline" className="text-blue-600 border-blue-300 dark:border-blue-700 gap-1"><Play className="w-3 h-3" />Running</Badge>;
    case 'waiting_approval':
      return <Badge variant="outline" className="text-amber-600 border-amber-300 dark:border-amber-700 gap-1"><Timer className="w-3 h-3" />Awaiting Approval</Badge>;
    case 'waiting':
      return <Badge variant="outline" className="text-blue-600 border-blue-300 dark:border-blue-700 gap-1"><Timer className="w-3 h-3" />Waiting</Badge>;
    case 'cancelled':
      return <Badge variant="outline" className="text-gray-500 border-gray-300 dark:border-gray-700 gap-1"><Ban className="w-3 h-3" />Cancelled</Badge>;
    default:
      return <Badge variant="outline" className="text-muted-foreground gap-1"><Clock className="w-3 h-3" />{status}</Badge>;
  }
}

function formatDuration(startedAt: string, completedAt?: string | null): string {
  if (!completedAt) return '—';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return '<1s';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.round((ms % 3600000) / 60000)}m`;
}

function getTriggerSource(signal: any): { label: string; icon: string } {
  if (!signal) return { label: '—', icon: '' };
  const source = signal.signal_source || signal.metadata?.signal_source || '';
  if (source === 'request') {
    const category = signal.category || signal.metadata?.category || '';
    if (category === 'k8s') return { label: 'K8s Request', icon: 'kubernetes' };
    if (category === 'cloud') return { label: 'AWS Request', icon: 'aws' };
    if (category === 'gcp') return { label: 'GCP Request', icon: 'gcp' };
    if (category === 'cloudflare') return { label: 'Cloudflare Request', icon: 'cloudflare' };
    if (category === 'pagerduty') return { label: 'PagerDuty Request', icon: 'pagerduty' };
    if (category === 'datadog') return { label: 'Datadog Request', icon: 'datadog' };
    if (category === 'argocd') return { label: 'ArgoCD Request', icon: 'argocd' };
    if (category === 'argo-rollouts') return { label: 'Argo Rollouts Request', icon: 'argo-rollouts' };
    if (category === 'fluxcd') return { label: 'Flux CD Request', icon: 'fluxcd' };
    if (category === 'karpenter') return { label: 'Karpenter Request', icon: 'karpenter' };
    if (category === 'kyverno') return { label: 'Kyverno Request', icon: 'kyverno' };
    if (category === 'trivy') return { label: 'Trivy Request', icon: 'trivy' };
    if (category === 'github') return { label: 'GitHub Request', icon: 'github' };
    if (category === 'gitlab') return { label: 'GitLab Request', icon: 'gitlab' };
    if (category === 'helm') return { label: 'Helm Request', icon: 'helm' };
    if (category === 'vercel') return { label: 'Vercel Request', icon: 'vercel' };
    if (category === 'railway') return { label: 'Railway Request', icon: 'railway' };
    if (category === 'flyio') return { label: 'Fly.io Request', icon: 'flyio' };
    if (category === 'nebius') return { label: 'Nebius Request', icon: 'nebius' };
    if (category === 'beam') return { label: 'Beam Request', icon: 'beam' };
    if (category === 'daytona') return { label: 'Daytona Request', icon: 'daytona' };
    if (category === 'supabase') return { label: 'Supabase Request', icon: 'supabase' };
    if (category === 'planetscale') return { label: 'PlanetScale Request', icon: 'planetscale' };
    if (category === 'neon') return { label: 'Neon Request', icon: 'neon' };
    if (category === 'clickhouse') return { label: 'ClickHouse Request', icon: 'clickhouse' };
    if (category === 'jenkins') return { label: 'Jenkins Request', icon: 'jenkins' };
    if (category === 'circleci') return { label: 'CircleCI Request', icon: 'circleci' };
    if (category === 'sonarcloud') return { label: 'SonarCloud Request', icon: 'sonarcloud' };
    if (category === 'okta') return { label: 'Okta Request', icon: 'okta' };
    if (category === 'databricks') return { label: 'Databricks Request', icon: 'databricks' };
    if (category === 'terraform') return { label: 'Terraform Request', icon: 'terraform' };
    if (category === 'pulumi') return { label: 'Pulumi Request', icon: 'pulumi' };
    if (category === 'vault') return { label: 'Vault Request', icon: 'vault' };
    if (category === 'infisical') return { label: 'Infisical Request', icon: 'infisical' };
    return { label: 'Platform Request', icon: 'slack' };
  }
  if (source === 'github') return { label: 'GitHub', icon: 'github' };
  if (source === 'aws') return { label: 'AWS', icon: 'aws' };
  if (source === 'datadog') return { label: 'Datadog', icon: 'datadog' };
  if (source === 'cloudflare') return { label: 'Cloudflare', icon: 'cloudflare' };
  if (source === 'pagerduty') return { label: 'PagerDuty', icon: 'pagerduty' };
  if (source === 'posthog') return { label: 'PostHog', icon: 'posthog' };
  if (source === 'vercel') return { label: 'Vercel', icon: 'vercel' };
  if (source === 'railway') return { label: 'Railway', icon: 'railway' };
  if (source === 'flyio') return { label: 'Fly.io', icon: 'flyio' };
  if (source === 'nebius') return { label: 'Nebius', icon: 'nebius' };
  if (source === 'beam') return { label: 'Beam', icon: 'beam' };
  if (source === 'daytona') return { label: 'Daytona', icon: 'daytona' };
  if (source === 'supabase') return { label: 'Supabase', icon: 'supabase' };
  if (source === 'planetscale') return { label: 'PlanetScale', icon: 'planetscale' };
  if (source === 'neon') return { label: 'Neon', icon: 'neon' };
  if (source === 'clickhouse') return { label: 'ClickHouse', icon: 'clickhouse' };
  if (source === 'jenkins') return { label: 'Jenkins', icon: 'jenkins' };
  if (source === 'circleci') return { label: 'CircleCI', icon: 'circleci' };
  if (source === 'sonarcloud') return { label: 'SonarCloud', icon: 'sonarcloud' };
  if (source === 'okta') return { label: 'Okta', icon: 'okta' };
  if (source === 'databricks') return { label: 'Databricks', icon: 'databricks' };
  if (source === 'gcp') return { label: 'Google Cloud', icon: 'gcp' };
  if (source === 'terraform') return { label: 'Terraform Cloud', icon: 'terraform' };
  if (source === 'pulumi') return { label: 'Pulumi Cloud', icon: 'pulumi' };
  if (source === 'vault') return { label: 'HashiCorp Vault', icon: 'vault' };
  if (source === 'infisical') return { label: 'Infisical', icon: 'infisical' };
  if (source === 'custom_webhook') return { label: 'Custom Webhook', icon: 'custom' };
  if (source === 'karpenter') return { label: 'Karpenter', icon: 'karpenter' };
  if (source === 'kyverno') return { label: 'Kyverno', icon: 'kyverno' };
  if (source === 'trivy') return { label: 'Trivy', icon: 'trivy' };
  if (source === 'schedule') return { label: 'Schedule', icon: 'schedule' };

  // Fallback: check signal_type for Kubernetes-specific signals
  const signalType = signal.signal_type || '';
  if (signalType === 'push' || signalType.startsWith('pull_request') || signalType.startsWith('workflow_run')) {
    return { label: 'GitHub', icon: 'github' };
  }

  return { label: 'Kubernetes', icon: 'kubernetes' };
}

function IntegrationIcon({ integration, isDark, size = 14, iconUrl }: { integration: string; isDark: boolean; size?: number; iconUrl?: string }) {
  const s = { width: size, height: size };
  const cls = 'rounded';
  if ((integration === 'custom' || integration.startsWith('custom:')) && iconUrl) {
    return <img src={iconUrl} alt="Custom" style={s} className={`${cls} object-contain`} />;
  }
  switch (integration) {
    case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" style={s} className={cls} />;
    case 'gitlab': return <img src={gitlabIcon} alt="GitLab" style={s} className={cls} />;
    case 'slack': return <img src={slackColorIcon} alt="Slack" style={s} className={cls} />;
    case 'pagerduty': return <img src={pagerdutyColorIcon} alt="PagerDuty" style={s} className={cls} />;
    case 'confluence': return <img src={confluenceIcon} alt="Confluence" style={s} className={cls} />;
    case 'jira': return <img src={jiraIcon} alt="Jira" style={s} className={cls} />;
    case 'linear': return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" style={s} className={cls} />;
    case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" style={s} className={cls} />;
    case 'aws': case 'aws-cost': case 'aws-ops': case 'aws-security': case 'aws-data': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" style={s} className={cls} />;
    case 'argocd': return <img src={argocdIcon} alt="ArgoCD" style={s} className={cls} />;
    case 'argo-rollouts': return <img src={argocdIcon} alt="Argo Rollouts" style={s} className={cls} />;
    case 'fluxcd': return <img src={fluxcdIcon} alt="Flux CD" style={s} className={cls} />;
    case 'karpenter': return <img src={karpenterIcon} alt="Karpenter" style={s} className={cls} />;
    case 'kyverno': return <img src={kyvernoIcon} alt="Kyverno" style={s} className={cls} />;
    case 'trivy': return <img src={trivyIcon} alt="Trivy" style={s} className={cls} />;
    case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" style={s} className={cls} />;
    case 'posthog': return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" style={s} className={`${cls} object-contain`} />;
    case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'railway': return <img src={railwayIcon} alt="Railway" style={{ width: size * 0.8, height: size * 0.8, filter: isDark ? 'invert(1)' : 'none' }} className={`${cls} object-contain`} />;
    case 'flyio': return <img src={flyioIcon} alt="Fly.io" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'nebius': return <img src={nebiusIcon} alt="Nebius" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'beam': return <BeamIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'daytona': return <DaytonaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'supabase': return <img src={supabaseIcon} alt="Supabase" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'planetscale': return <PlanetScaleIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'neon': return <img src={neonIcon} alt="Neon" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'circleci': return <CircleCIIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'sonarcloud': return <SonarCloudIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'okta': return <OktaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'databricks': return <img src={databricksIcon} alt="Databricks" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'gcp': case 'gcp-cost': case 'gcp-security': case 'gcp-data': return <GCPExecIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'terraform': return <img src={terraformIcon} alt="Terraform" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'vault': return <img src={vaultIcon} alt="Vault" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'infisical': return <img src={infisicalIcon} alt="Infisical" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'cloudflare': return <img src={cloudflareIcon} alt="Cloudflare" style={s} className={cls} />;
    case 'kestrel': return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" style={s} className={cls} />;
    case 'kubernetes': return <ShipWheel style={s} className="text-blue-500" />;
    case 'schedule': return <Clock style={{ width: size * 0.85, height: size * 0.85 }} className="text-zinc-900 dark:text-zinc-100" />;
    case 'approval': return <ShieldCheck style={{ width: size * 0.85, height: size * 0.85 }} className="text-amber-500" />;
    case 'custom': return <Webhook style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    default:
      if (integration.startsWith('custom:')) return <Webhook style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
      return <Workflow style={{ width: size * 0.85, height: size * 0.85 }} className="text-muted-foreground" />;
  }
}

function IntegrationStack({ integrations, isDark, customBlocks }: { integrations: string[]; isDark: boolean; customBlocks?: CustomBlock[] }) {
  if (!integrations || integrations.length === 0) return null;

  const resolveIconUrl = (integ: string) => {
    if (!customBlocks || customBlocks.length === 0) return undefined;
    if (integ.startsWith('custom:')) {
      const iName = integ.replace('custom:', '');
      // Try exact match on integration_name, block name, or label
      let cb = customBlocks.find(b => (b.integration_name || '') === iName || b.name === iName);
      // Fallback: case-insensitive match
      if (!cb) cb = customBlocks.find(b =>
        (b.integration_name || '').toLowerCase() === iName.toLowerCase() ||
        b.name.toLowerCase() === iName.toLowerCase()
      );
      // Fallback: partial match (label often contains the integration name)
      if (!cb) cb = customBlocks.find(b =>
        iName.toLowerCase().includes((b.integration_name || '').toLowerCase()) ||
        (b.integration_name || '').toLowerCase().includes(iName.toLowerCase())
      );
      if (cb) {
        const light = cb.icon_url || '';
        const dark = cb.icon_url_dark || light;
        return isDark ? (dark || light) : (light || dark) || undefined;
      }
    }
    if (integ === 'custom') {
      const cb = customBlocks.find(b => (b.icon_url || b.icon_url_dark));
      if (cb) {
        const light = cb.icon_url || '';
        const dark = cb.icon_url_dark || light;
        return isDark ? (dark || light) : (light || dark) || undefined;
      }
    }
    return undefined;
  };

  if (integrations.length === 1) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/50 shrink-0">
        <IntegrationIcon integration={integrations[0]} isDark={isDark} size={20} iconUrl={resolveIconUrl(integrations[0])} />
      </div>
    );
  }

  const positions: { top: number; left: number }[] = [];
  if (integrations.length === 2) {
    positions.push({ top: 0, left: 0 }, { top: 14, left: 19 });
  } else {
    positions.push({ top: 0, left: 0 }, { top: 0, left: 19 });
    const row2Count = integrations.length - 2;
    const row2StartLeft = row2Count === 1 ? 10 : 0;
    for (let i = 2; i < integrations.length; i++) {
      positions.push({ top: 19, left: row2StartLeft + (i - 2) * 19 });
    }
  }

  return (
    <div className="relative w-12 h-10 shrink-0">
      {integrations.map((integ, i) => (
        <div
          key={integ}
          className="absolute bg-background border rounded-md p-0.5 shadow-sm flex items-center justify-center"
          style={{
            width: 22, height: 22,
            top: positions[i]?.top ?? 0,
            left: positions[i]?.left ?? 0,
            zIndex: integrations.length - i,
          }}
        >
          <IntegrationIcon integration={integ} isDark={isDark} size={16} iconUrl={resolveIconUrl(integ)} />
        </div>
      ))}
    </div>
  );
}

const WorkflowExecutions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [executions, setExecutions] = useState<ExecutionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [initialLoad, setInitialLoad] = useState(true);
  const [workflowOptions, setWorkflowOptions] = useState<{ id: string; name: string }[]>([]);
  const [customBlocks, setCustomBlocks] = useState<CustomBlock[]>([]);

  const parseArray = (key: string) => {
    const v = searchParams.get(key);
    return v ? v.split(',').filter(Boolean) : [];
  };

  const [statusFilter, setStatusFilter] = useState<string[]>(parseArray('status'));
  const [workflowFilter, setWorkflowFilter] = useState<string[]>(parseArray('workflow_id'));
  const [triggerFilter, setTriggerFilter] = useState<string[]>(parseArray('trigger_source'));
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  const [fetchGen, setFetchGen] = useState(0);
  const triggerFetch = useCallback(() => setFetchGen(g => g + 1), []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const prevFiltersRef = useRef({ statusFilter, workflowFilter, triggerFilter, debouncedSearch });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const changed = JSON.stringify(prev.statusFilter) !== JSON.stringify(statusFilter) ||
      JSON.stringify(prev.workflowFilter) !== JSON.stringify(workflowFilter) ||
      JSON.stringify(prev.triggerFilter) !== JSON.stringify(triggerFilter) ||
      prev.debouncedSearch !== debouncedSearch;
    if (changed) {
      prevFiltersRef.current = { statusFilter, workflowFilter, triggerFilter, debouncedSearch };
      setPage(1);
      triggerFetch();
    }
  }, [statusFilter, workflowFilter, triggerFilter, debouncedSearch, triggerFetch]);

  const paramsRef = useRef({ page, statusFilter, workflowFilter, triggerFilter, debouncedSearch });
  useEffect(() => {
    paramsRef.current = { page, statusFilter, workflowFilter, triggerFilter, debouncedSearch };
  }, [page, statusFilter, workflowFilter, triggerFilter, debouncedSearch]);

  const doFetch = useCallback(async (silent = false) => {
    if (!user) return;
    if (!silent) setInitialLoad(true);
    try {
      const p = paramsRef.current;
      const params: Record<string, string | number> = { page: p.page, page_size: PAGE_SIZE };
      if (p.statusFilter.length) params.status = p.statusFilter.join(',');
      if (p.workflowFilter.length) params.workflow_id = p.workflowFilter.join(',');
      if (p.triggerFilter.length) params.trigger_source = p.triggerFilter.join(',');
      if (p.debouncedSearch) params.search = p.debouncedSearch;

      const result = await apiClient.listAllExecutions(params);
      setExecutions(result.executions || []);
      setTotal(result.total);
      if (result.workflow_options) setWorkflowOptions(result.workflow_options);
    } catch (err) {
      console.error('Failed to load executions:', err);
    } finally {
      setInitialLoad(false);
    }
  }, [user]);

  useEffect(() => { doFetch(); }, [doFetch, page, fetchGen]);

  useEffect(() => {
    if (user) apiClient.listCustomBlocks().then(setCustomBlocks).catch(() => {});
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => doFetch(true), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [doFetch]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (page > 1) params.page = String(page);
    if (statusFilter.length) params.status = statusFilter.join(',');
    if (workflowFilter.length) params.workflow_id = workflowFilter.join(',');
    if (triggerFilter.length) params.trigger_source = triggerFilter.join(',');
    if (debouncedSearch) params.search = debouncedSearch;
    setSearchParams(params, { replace: true });
  }, [page, statusFilter, workflowFilter, triggerFilter, debouncedSearch, setSearchParams]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters = statusFilter.length > 0 || workflowFilter.length > 0 || triggerFilter.length > 0 || debouncedSearch;

  const clearFilters = () => {
    setStatusFilter([]);
    setWorkflowFilter([]);
    setTriggerFilter([]);
    setSearchQuery('');
    setPage(1);
  };

  if (authLoading || initialLoad) {
    return <div className="flex items-center justify-center min-h-[calc(100vh-140px)]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const workflowDropdownOptions = workflowOptions.map(w => ({ value: w.id, label: w.name }));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search workflow name or error..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>

          <MultiSelectDropdown
            label="All Statuses"
            options={STATUS_OPTIONS}
            selected={statusFilter}
            onChange={setStatusFilter}
            className="w-[160px]"
          />

          <MultiSelectDropdown
            label="All Workflows"
            options={workflowDropdownOptions}
            selected={workflowFilter}
            onChange={setWorkflowFilter}
            className="w-[200px]"
          />

          <MultiSelectDropdown
            label="All Sources"
            options={TRIGGER_OPTIONS}
            selected={triggerFilter}
            onChange={setTriggerFilter}
            className="w-[170px]"
            renderOption={(o) => {
              const iconKey = TRIGGER_OPTIONS.find(t => t.value === o.value)?.icon;
              return (
                <span className="flex items-center gap-1.5">
                  {iconKey && <IntegrationIcon integration={iconKey} isDark={isDark} size={14} />}
                  {o.label}
                </span>
              );
            }}
          />

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 gap-1 text-xs">
              <X className="w-3 h-3" />Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Table. When there is nothing to render, show a Card empty state
          instead of the table: the 6-column header forces the table wider
          than a phone viewport, so a centered colSpan cell renders off-screen
          on mobile — and the Card treatment matches the Workflows and
          Workflow Requests pages. */}
      {initialLoad ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : executions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center flex flex-col items-center justify-center">
            {hasActiveFilters ? (
              <p className="text-sm text-muted-foreground">No executions match the current filters.</p>
            ) : (
              <>
                <h3 className="text-lg font-medium">No executions yet</h3>
                <p className="text-muted-foreground mt-1 max-w-lg">
                  When your workflows run — triggered by signals, requests, or tests — each execution will appear here with step-level details.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
      <Card>
        <div className="min-h-[520px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workflow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.map(exec => {
                  const signal = typeof exec.trigger_signal === 'string'
                    ? (() => { try { return JSON.parse(exec.trigger_signal); } catch { return null; } })()
                    : exec.trigger_signal;
                  const isTest = signal?.test_execution === true || signal?.metadata?.test_execution === true;

                  return (
                    <TableRow
                      key={exec.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/workflows/${exec.workflow_id}/executions/${exec.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <IntegrationStack integrations={exec.workflow_integrations || []} isDark={isDark} customBlocks={customBlocks} />
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate" title={exec.workflow_name || exec.workflow_id}>
                              {exec.workflow_name || exec.workflow_id.slice(0, 8)}
                            </div>
                            <div className="text-xs text-muted-foreground">{exec.id.slice(0, 8)}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getStatusBadge(exec.status)}
                          {isTest && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400">Test</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const trigger = getTriggerSource(signal);
                          return (
                            <div className="flex items-center gap-1.5">
                              {trigger.icon && <IntegrationIcon integration={trigger.icon} isDark={isDark} size={14} />}
                              <span className="text-sm text-muted-foreground">{trigger.label}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(exec.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(exec.started_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{formatDuration(exec.started_at, exec.completed_at)}</span>
                      </TableCell>
                      <TableCell>
                        {exec.error_message && (
                          <span className="text-sm text-destructive truncate block max-w-md" title={exec.error_message}>
                            {exec.error_message}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
      )}
    </div>
  );
};

export default WorkflowExecutions;
